import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import StudentSidebar from "../components/studentsidebar";
import Navbar from "../components/studentnavbar";
import { useSidebar } from '../components/SidebarContext';
import api from "../services/api";

const CompletedTests = () => {
  const { isSidebarOpen } = useSidebar();
  const sidebarWidth = isSidebarOpen ? '15.6rem' : '4.4rem';
  const navigate = useNavigate();
  
  const [tests, setTests] = useState([]);
  const [filteredTests, setFilteredTests] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStudentCompletedTests = async () => {
      try {
        console.log('Starting to fetch student tests...');
        
        // Get student ID from localStorage - check all possible keys
        let studentId = localStorage.getItem('id'); // This is where it's stored in login
        
        // If not found, try getting from student object
        if (!studentId) {
          const studentData = localStorage.getItem('student');
          if (studentData) {
            try {
              const student = JSON.parse(studentData);
              studentId = student._id || student.id;
            } catch (parseError) {
              console.error('Error parsing student data:', parseError);
            }
          }
        }
        
        if (!studentId) {
          setError("Student ID not found. Please log in again.");
          setLoading(false);
          // Optionally redirect to login
          // navigate('/student-login');
          return;
        }
        
        console.log('Student ID found:', studentId);
        console.log('Student ID type:', typeof studentId);
        console.log('Student ID length:', studentId?.length);
        
        // Debug: Log what's in localStorage
        console.log('All localStorage keys:', Object.keys(localStorage));
        console.log('localStorage.id:', localStorage.getItem('id'));
        console.log('localStorage.student:', localStorage.getItem('student'));
        
        // Send student ID as query parameter
        const response = await api.get(`/api/results/student-completed-tests?studentId=${studentId}`);
        console.log('Response received:', response.data);
        
        // Format the response data
        const formattedTests = response.data.map((test, index) => ({
          id: test.id,
          testName: test.testName || `Test ${index + 1}`,
          testNumber: test.testNumber,
          overallPercentage: test.overallPercentage,
          grade: test.grade,
          attemptedAt: test.attemptedAt,
          rawScore: test.rawScore,
          totalQuestions: test.totalQuestions,
        }));
        
        console.log('Formatted tests:', formattedTests);
        setTests(formattedTests);
        setFilteredTests(formattedTests);
      } catch (err) {
        console.error('Detailed error:', err);
        console.error('Error message:', err.message);
        console.error('Error response:', err.response?.data);
        console.error('Error status:', err.response?.status);
        
        if (err.code === 'ECONNABORTED') {
          setError("Request timed out. Please try again.");
        } else if (err.response?.status === 404) {
          setError("Endpoint not found. Please contact support.");
        } else if (err.response?.status === 401) {
          setError("Authentication required. Please log in again.");
          // Optionally redirect to login
          // navigate('/student-login');
        } else if (err.response?.status === 400) {
          setError("Invalid request. Please log in again.");
          // Optionally redirect to login
          // navigate('/student-login');
        } else {
          setError("Failed to fetch test data. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchStudentCompletedTests();
  }, []);

  useEffect(() => {
    const filtered = tests.filter(test => {
      const testName = (test.testName || '').toLowerCase();
      const grade = (test.grade || '').toLowerCase();
      const testNumber = (test.testNumber || '').toString().toLowerCase();
      const score = (test.overallPercentage || '').toString().toLowerCase();
      const attemptedAt = new Date(test.attemptedAt).toLocaleDateString("en-GB", {
        year: "numeric",
        month: "short",
        day: "numeric",
      }).toLowerCase();
      const search = searchQuery.toLowerCase();

      return (
        testName.includes(search) ||
        grade.includes(search) ||
        testNumber.includes(search) ||
        score.includes(search) ||
        attemptedAt.includes(search)
      );
    });
    setFilteredTests(filtered);
  }, [searchQuery, tests]);

  const styles = {
    container: {
      display: 'flex',
      height: '100vh',
      overflow: 'hidden',
      minHeight: "100vh",
      background: "linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF)",
      fontFamily: "'Roboto', sans-serif",
    },
    sidebar: {
      width: sidebarWidth,
      borderRadius: '30px',
      height: '100vh',
      flexShrink: 0,
      position: 'fixed',
      top: 0,
      left: 0,
      zIndex: 10,
      transition: 'width 0.3s ease',
      fontFamily: "'Roboto', sans-serif",
    },
    content: {
      flex: 1,
      padding: '15px',
      margin: '6rem 2rem 2rem 2rem',
      backgroundColor: 'white',
      transition: 'margin-left 0.3s ease',
      height: 'calc(100vh - 8rem)',
      borderTopLeftRadius: '1rem',
      borderTopRightRadius: '1rem',
      borderBottomLeftRadius: '1rem',
      borderBottomRightRadius: '1rem',
      maxWidth: `calc(100vw - ${sidebarWidth} - 4rem)`,
      width: '100%',
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      fontFamily: "'Roboto', sans-serif",
    },
    headerContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-end',
      alignItems: 'center',
      marginBottom: '16px',
      width: '100%',
      flexShrink: 0,
      fontFamily: "'Roboto', sans-serif",
    },
    searchFilterGroup: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '8px',
      fontFamily: "'Roboto', sans-serif",
    },
    searchInput: {
      width: '100%',
      maxWidth: '300px',
      padding: '6px 12px',
      borderRadius: '30px',
      border: '1px solid #ddd',
      fontSize: '14px',
      outline: 'none',
      transition: 'border-color 0.3s ease',
      fontFamily: "'Roboto', sans-serif",
    },
    tableContainer: {
      flex: 1,
      overflowY: 'auto',
      overflowX: 'auto',
      border: 'none',
      borderRadius: '8px',
      maxHeight: 'calc(100vh - 12rem)',
      height: 'auto',
      fontFamily: "'Roboto', sans-serif",
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      border: 'none',
      fontSize: '12px',
      fontFamily: "'Roboto', sans-serif",
    },
    headerRow: {
      background: 'linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF)',
      position: 'sticky',
      top: 0,
      zIndex: 2,
      fontFamily: "'Roboto', sans-serif",
    },
    th: {
      padding: '12px 10px',
      fontWeight: '500',
      textAlign: 'center',
      color: '#6B21A8',
      fontSize: '12px',
      borderBottom: '2px solid #e5e7eb',
      fontFamily: "'Roboto', sans-serif",
    },
    row: {
      borderRadius: '8px',
      fontFamily: "'Roboto', sans-serif",
    },
    td: {
      padding: '10px 12px',
      verticalAlign: 'middle',
      textAlign: 'center',
      color: '#333',
      fontFamily: "'Roboto', sans-serif",
    },
    actionButton: {
      backgroundColor: 'transparent',
      color: '#cc8888',
      border: 'none',
      borderRadius: '2px',
      padding: '2px',
      cursor: 'pointer',
      transition: 'color 0.3s ease, transform 0.3s ease',
      fontFamily: "'Roboto', sans-serif",
    },
    notificationContainer: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '16px',
      backgroundColor: '#fffffff',
      color: '#6B21A8',
      fontFamily: "'Roboto', sans-serif",
      animation: 'slideInFromRight 0.5s ease-out',
    },
  };

  // Updated handleViewResult function with student role information
  const handleViewResult = (testId) => {
    navigate(`/stored-result/${testId}`, { 
      state: { 
        from: '/Result',
        isAdmin: false,
        userRole: 'student'
      } 
    });
  };

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes slideInFromRight {
          0% {
            transform: translateX(100%);
            opacity: 0;
          }
          100% {
            transform: translateX(0);
            opacity: 1;
          }
        }
        input:focus {
          border-color: #6B21A8 !important;
          font-family: 'Roboto', sans-serif !important;
        }
        .custom-table {
          border: none !important;
          font-family: 'Roboto', sans-serif !important;
        }
        .custom-table th,
        .custom-table td {
          border: none !important;
          font-family: 'Roboto', sans-serif !important;
        }
        .custom-table tbody tr:nth-child(odd) {
          background-color: #FFFFFF;
          font-family: 'Roboto', sans-serif !important;
        }
        .custom-table tbody tr:nth-child(even) {
          background: linear-gradient(to right, #FAFCFF, #FBF6FF, #FFF8FB);
          font-family: 'Roboto', sans-serif !important;
        }
        .custom-table tbody tr:hover {
          background: linear-gradient(to right, #F0F5FF, #F5EDFF, #FFF0F5) !important;
          font-family: 'Roboto', sans-serif !important;
        }
        .action-button:hover {
          color: #b36666 !important;
          transform: scale(1.1);
          font-family: 'Roboto', sans-serif !important;
        }
        .table-container::-webkit-scrollbar {
          width: 12px;
          height: 12px;
        }
        .table-container::-webkit-scrollbar-track {
          background: #f8f9fa;
          border-radius: 6px;
          font-family: 'Roboto', sans-serif !important;
        }
        .table-container::-webkit-scrollbar-thumb {
          background: #6c757d;
          border-radius: 6px;
          border: 2px solid #f8f9fa;
          font-family: 'Roboto', sans-serif !important;
        }
        .table-container::-webkit-scrollbar-thumb:hover {
          background: #495057;
          font-family: 'Roboto', sans-serif !important;
        }
        .table-container::-webkit-scrollbar-corner {
          background: #f8f9fa;
          font-family: 'Roboto', sans-serif !important;
        }
        .table-container {
          scrollbar-width: thin;
          scrollbar-color: #6c757d #f8f9fa;
          font-family: 'Roboto', sans-serif !important;
        }
        .search-input {
          font-family: 'Roboto', sans-serif !important;
        }
      `}</style>
      <div style={styles.sidebar}>
        <StudentSidebar />
      </div>

      <div style={{ marginLeft: sidebarWidth, width: `calc(100% - ${sidebarWidth})`, transition: 'all 0.3s ease', fontFamily: "'Roboto', sans-serif" }}>
        <Navbar />
        <div style={styles.content}>
          <div style={styles.headerContainer}>
            <div style={styles.searchFilterGroup}>
              <input
                type="text"
                placeholder="Search tests..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={styles.searchInput}
                className="search-input"
                aria-label="Search tests"
              />
            </div>
          </div>
          
          {loading ? (
            <div style={styles.notificationContainer}>
              <p>Loading...</p>
            </div>
          ) : error ? (
            <div style={styles.notificationContainer}>
              <p>{error}</p>
            </div>
          ) : filteredTests.length === 0 ? (
            <div style={styles.notificationContainer}>
              <p>No completed tests available.</p>
            </div>
          ) : (
            <div style={styles.tableContainer} className="table-container">
              <table className="custom-table" style={styles.table}>
                <thead style={styles.headerRow}>
                  <tr>
                    <th style={styles.th}>Sr.</th>
                    <th style={{ ...styles.th, textAlign: 'center' }}>Test Name</th>
                    <th style={styles.th}>Score (%)</th>
                    <th style={styles.th}>Raw Score</th>
                    <th style={styles.th}>Total Questions</th>
                    <th style={styles.th}>Grade</th>
                    <th style={styles.th}>Date Attempted</th>
                    <th style={styles.th}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTests.map((test, index) => (
                    <tr
                      key={test.id}
                      style={styles.row}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(to right, #F0F5FF, #F5EDFF, #FFF0F5)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = index % 2 === 0 ? '#FFFFFF' : 'linear-gradient(to right, #FAFCFF, #FBF6FF, #FFF8FB)';
                      }}
                    >
                      <td style={styles.td}>{index + 1}</td>
                      <td style={{ ...styles.td, textAlign: 'left' }}>{test.testName}</td>
                      <td style={styles.td}>{test.overallPercentage}%</td>
                      <td style={styles.td}>{test.rawScore}/{test.totalQuestions}</td>
                      <td style={styles.td}>{test.totalQuestions}</td>
                      <td style={styles.td}>{test.grade}</td>
                      <td style={styles.td}>
                        {new Date(test.attemptedAt).toLocaleDateString("en-GB", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </td>
                      <td style={styles.td}>
                        <button
                          className="action-button"
                          style={styles.actionButton}
                          onClick={() => handleViewResult(test.id)}
                          title="View test result"
                          aria-label="View test result"
                        >
                          <FontAwesomeIcon icon={faEye} style={{ fontSize: '17px', fontFamily: "'Roboto', sans-serif" }} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompletedTests;